import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import classNames from "classnames";
import { useStoreState } from "pullstate";
import { parseCookies } from "nookies";
import Image from "next/image";
import Head from "next/head";
import dynamic from "next/dynamic";

import Custom404 from "src/pages/404";
import useRequest from "@/helpers/useRequest";
import { apiClient } from "@/helpers/apiClient";
import { apiPath } from "@/helpers/apiPath";
import { EpisodeInterface } from "@/helpers/pageHelpers/Episode/interface";
import { applicationRoutes } from "@/helpers/applicationRouting";
import { routePath } from "@/helpers/routePath";
import Container from "@/components/Container";
import PageWrapper from "@/components/PageWrapper";
import MetaHead from "@/components/MetaHead";
import { Schema } from "@/components/MetaHead/Head.interface";
import { GridColumns } from "@/components/Container/Container.interface";
import Typography from "@/components/Typography";
import Tag from "@/components/Tag";
import EpisodePagePlayer from "@/components/Players/EpisodePagePlayer";
import OtherEpisodes from "@/components/OtherEpisodes";
import SkeletonLoader from "@/components/SkeletonLoader";
import Recommended from "@/components/Recommended";
import ShareModal from "@/components/Modals/ShareModal";
import PodcastTopAd from "@/components/Advertisement/PodcastTop";
import { UIStore } from "@/store/UIStore";
import SimilarPodcasts from "@/components/SimilarPodcasts";
import { getImgixURL } from "@/helpers/getImgixURL";
import { VoteSection } from "@/components/VoteSection";
import { ContentSourceEnum } from "@/components/BookmarkButton/BookmarkButton.interface";
import {
  ContentType,
  VoteSectionVariant,
} from "@/components/VoteSection/VoteSection.interface";
import LargePodcastPageIcon from "@/icons/large-podcast-page.svg";
import PodcastPageIcon from "@/icons/podcast-page.svg";
import Tabs from "@/components/Tabs";
import Hosts from "@/components/Hosts";
import { getParsedJSON } from "@/helpers/getParsedJSON";

import styles from "./Episode.module.scss";

const SidebarAd = dynamic(import("@/components/Advertisement/Sidebar"), {
  ssr: false,
});

const PodcastBottomAd = dynamic(import("@/components/Advertisement/Sidebar"), {
  ssr: false,
});

export const Episode: React.FC<EpisodeInterface> = ({
  initialData,
  episodes,
  podcastSeo,
}) => {
  const router = useRouter();

  const { slug } = router.query;
  const { data, mutate, error } = useRequest(apiPath.PODCAST_EPISODE(slug), {
    initialData,
    revalidateOnMount: true,
  });

  const [isShareModalOpen, openShareModal] = useState<boolean>(false);

  const { userLoggedIn } = useStoreState(UIStore, (store) => store);

  useEffect(() => {
    mutate();
  }, [userLoggedIn, mutate]);

  if (router.isFallback) {
    return (
      <PageWrapper className={styles.pageWrapper}>
        <div className={styles.header}>
          <Container grid={GridColumns.Three} className={styles.container}>
            <Typography
              variant="heading"
              component="h1"
              className={styles.pageTitle}
            >
              <a className={styles.podcastLink}>
                <SkeletonLoader height={50} />
              </a>
            </Typography>

            <div className={styles.episodeInfo}>
              <div className={styles.box}>
                <div className={styles.artwork}>
                  <SkeletonLoader />
                </div>

                <div className={styles.meta}>
                  <Typography component="h2" className={styles.title}>
                    <SkeletonLoader height={50} />
                  </Typography>

                  <SkeletonLoader height={350} />
                </div>
              </div>

              <div className={styles.description}>
                <SkeletonLoader />
              </div>
            </div>
          </Container>
        </div>

        <Container grid={GridColumns.Three}>
          <div className={styles.tracksRelated}>
            <div className={styles.sectionWrapper}>
              <SkeletonLoader />
              <SkeletonLoader />
              <SkeletonLoader />
            </div>
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (!initialData || error || !slug) {
    return <Custom404 />;
  }

  const transcript = getParsedJSON(data.transcript);
  const mediaInfo = data.media;
  const podcastInfo = data?.podcast;
  const isHostAvailable = !!data?.hosts?.length;
  const podcastLink = applicationRoutes.PODCAST + podcastInfo.slug;
  const podcastLinkAs = routePath.PODCAST.href;
  const cookies = parseCookies();
  const disableRobots =
    data.slug.includes("traktujmy-sie") &&
    data.podcast.slug !== "newonce-radio-specials";

  const apiToken = cookies?.token;
  const audioURL = apiToken
    ? `${mediaInfo.url}?api_token=${apiToken}`
    : mediaInfo.url;

  const img = getImgixURL(data?.picture);

  const metaData = {
    ...data?.headMetaData,
    ogFbImage: data?.headMetaData?.ogFbImage || podcastSeo.ogFbImage,
    ogTwImage: data?.headMetaData?.ogTwImage || podcastSeo.ogTwImage,
  };

  const pageData = {
    headerLabel: initialData.podcast.title,
    fallbackHistoryBack: {
      backUrl: podcastLink,
      as: podcastLinkAs,
    },
  };

  const schemaData = {
    type: Schema.Episode,
    title: data.title,
    tags: data.tags,
    slug: mediaInfo.slug,
    author: data?.hosts[0]?.name,
  };

  const mediaMetaData = {
    title: mediaInfo.subtitle,
    subTitle: mediaInfo.title,
    artwork: data.picture,
    slug: mediaInfo.slug,
    podcast: { slug: podcastInfo.slug },
    tags: data.tags.map(({ title }) => title),
    authors: data.hosts.map(({ name }) => name),
    publishedAt: data.publishedAt,
  };

  const toggleModal = () => {
    openShareModal((prevState) => !prevState);
  };

  return (
    <>
      <PageWrapper pageData={pageData} className={styles.pageWrapper}>
        <MetaHead schemaData={schemaData} metaData={metaData} />
        {disableRobots && (
          <Head>
            <meta name="robots" content="noindex" />
          </Head>
        )}
        <Container grid={GridColumns.Three} className={styles.container}>
          <div className={styles.ad}>
            <PodcastTopAd isEpisode />
          </div>
          <div className={classNames(styles.podcastPage, styles.small)}>
            <PodcastPageIcon />
            <Typography variant="medium">
              <Link href={podcastLink}>
                <a className={styles.podcastPageLink}>Strona podcastu</a>
              </Link>
            </Typography>
          </div>
          <Typography variant="h1" component="h1" className={styles.pageTitle}>
            {data?.podcast.title}
          </Typography>
          <div className={styles.header}>
            <Image
              src={`${img}?blur=230`}
              objectFit="cover"
              layout="fill"
              alt={data?.podcast.title}
              className={styles.backgroundImage}
            />
            <div className={styles.headingContent}>
              <div className={styles.imageBox}>
                <Image
                  src={img}
                  layout="fill"
                  objectFit="cover"
                  priority
                  alt={data?.podcast.title}
                  className={styles.image}
                />
              </div>
              <div className={styles.info}>
                <div className={styles.tags}>
                  {data.tags.map(({ title, id, slug }) => (
                    <Link
                      key={id}
                      href={`${applicationRoutes.TAG}[slug]`}
                      as={applicationRoutes.TAG + slug}
                    >
                      <a className={styles.link}>
                        <Tag title={title} key={id} className={styles.tag}>
                          {title}
                        </Tag>
                      </a>
                    </Link>
                  ))}
                </div>
                <Typography component="h2" className={styles.title}>
                  {data?.title}
                </Typography>
              </div>
              <div className={styles.player}>
                <EpisodePagePlayer
                  slug={data.slug}
                  publishedAt={data.publishedAt}
                  url={audioURL}
                  duration={mediaInfo.duration}
                  longVersionDuration={mediaInfo.longVersionDuration}
                  bookmarked={data.bookmarked}
                  shareAction={toggleModal}
                  metaData={mediaMetaData}
                  time={data.time}
                />
              </div>
            </div>
          </div>
          <div className={classNames(styles.podcastPage, styles.large)}>
            <LargePodcastPageIcon />
            <Typography variant="medium">
              <Link href={podcastLink}>
                <a className={styles.podcastPageLink}>Strona podcastu</a>
              </Link>
            </Typography>
          </div>

          <aside className={styles.sidebar}>
            {isHostAvailable && (
              <Hosts hosts={data.hosts} className={styles.hosts} />
            )}
            <div className={styles.desktop}>
              <SimilarPodcasts slug={data.podcast.slug} />
              <VoteSection
                source={ContentSourceEnum.Episodes}
                data={data}
                type={ContentType.EPISODE}
                className={styles.voteSection}
                variant={VoteSectionVariant.Sidebar}
              />
            </div>
            <SidebarAd />
          </aside>
          <Container className={styles.content}>
            <Tabs
              tracklist={data?.tracklist}
              description={data?.description}
              tags={data?.tags}
              slug={slug}
              className={styles.tabs}
              transcript={transcript}
              authors={data.hosts}
              title={data.title}
            />

            <div className={classNames(styles.sectionWrapper, styles.others)}>
              <OtherEpisodes slug={slug} episodes={episodes} />
            </div>
            <SimilarPodcasts slug={data.podcast.slug} isMobile />

            <VoteSection
              source={ContentSourceEnum.Episodes}
              data={data}
              type={ContentType.EPISODE}
              className={classNames(styles.voteSection, styles.mobile)}
            />
            <div className={styles.ad}>
              <PodcastBottomAd />
            </div>
          </Container>
        </Container>
        <Recommended />
        <ShareModal
          onClose={toggleModal}
          isOpen={isShareModalOpen}
          artwork={data?.picture}
          title={data?.title}
          subtitle={data?.podcast.title}
          isStoriesShareAvailable
        />
      </PageWrapper>
    </>
  );
};

export async function getStaticPaths() {
  const client = apiClient();

  const {
    data: { items },
  } = await client.get(apiPath.EPISODE_LIST({ perPage: 50 }));

  const paths =
    items &&
    items
      .filter((item) => item.type === "EPISODE")
      .map((episode) => ({
        params: { slug: episode.object.slug },
      }));

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const client = apiClient();

  try {
    const { data } = await client.get(apiPath.PODCAST_EPISODE(params.slug));
    const { data: episodes } = await client.get(
      apiPath.MORE_EPISODES({ slug: params.slug, page: 1 })
    );
    const { data: podcast } = await client.get(
      apiPath.PODCAST(data.podcast.slug)
    );

    return {
      props: {
        initialData: data,
        podcastSeo: podcast.headMetaData,
        episodes: episodes.items,
      },
      revalidate: 86400,
    };
  } catch (e) {
    return {
      props: {
        initialData: null,
      },
      revalidate: 43200,
    };
  }
}

export default Episode;
